import { MAX_WORK_ITEM_PRIORITY, TERMINAL_WORK_ITEM_STATUSES, type WorkItem, type WorkItemStatus } from '@deskworks/shared-types';
import { Check, MessageSquareText, Pause, Pencil, Play, RotateCcw, X, XCircle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Confirm } from '@/components/confirm';
import { NONE } from '@/components/desk-dialogs';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { WorkItemRow } from '@/components/work-item-row';
import { api } from '@/lib/api';
import { useAgents, useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { parsePriority, WORK_ITEM_STATUS } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

/** Leaves the agent or priority of the chosen items as they are. */
const KEEP = '__keep__';

/** What can be done to several Work Items at once, and to which of them (the same rules as on one item's page). */
const ACTIONS = {
  start: { can: (w: WorkItem) => (w.status === 'created' || w.status === 'blocked') && !w.paused && !!w.assignedAgentId, run: (id: string) => api.workItems.ready(id) },
  resume: { can: (w: WorkItem) => w.paused && !TERMINAL_WORK_ITEM_STATUSES.includes(w.status), run: (id: string) => api.workItems.resume(id) },
  pause: { can: (w: WorkItem) => !w.paused && !TERMINAL_WORK_ITEM_STATUSES.includes(w.status), run: (id: string) => api.workItems.pause(id) },
  retry: { can: (w: WorkItem) => w.status === 'failed', run: (id: string) => api.workItems.retry(id) },
  cancel: { can: (w: WorkItem) => !TERMINAL_WORK_ITEM_STATUSES.includes(w.status), run: (id: string) => api.workItems.cancel(id) },
} as const;
type Action = keyof typeof ACTIONS;

/** Runs `run` for every id; says how many worked and why the first one that didn't failed. */
async function each(ids: string[], run: (id: string) => Promise<unknown>) {
  const results = await Promise.allSettled(ids.map(run));
  const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed.length === ids.length) throw failed[0].reason;
  return { done: ids.length - failed.length, failed: failed.length };
}

/** Waits for a person's decision: approved or rejected together, with one comment for all. */
const decidable = (w: WorkItem) => w.status === 'needs_approval';
/** An instruction reaches every item not finished: a waiting or blocked one goes on with it, a running one hears it. */
const instructable = (w: WorkItem) => !TERMINAL_WORK_ITEM_STATUSES.includes(w.status);
/** One click ticks every item of the list that needs a person. */
const QUICK: readonly WorkItemStatus[] = ['needs_approval', 'waiting', 'blocked'];

/**
 * A list of Work Items to tick and act on together (docs/features/work-items.md#several-at-once): approve or reject
 * with a comment, start, continue, pause, try again, cancel, or set agent and priority. Each action only goes to the
 * ticked items it fits.
 */
export function SelectableWorkItemList({
  items,
  workspaceId,
  showDesk = true,
  row,
  className,
}: {
  /** In the order shown (a tree already flattened). */
  items: WorkItem[];
  workspaceId: string;
  showDesk?: boolean;
  /** How deep in the tree an item sits and what it shows at the end. */
  row?: (item: WorkItem) => { depth?: number; trailing?: ReactNode };
  className?: string;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Ticks of items no longer in the list (another filter, finished elsewhere) fall away.
  useEffect(() => {
    setSelected((s) => {
      const next = new Set(items.filter((w) => s.has(w.id)).map((w) => w.id));
      return next.size === s.size ? s : next;
    });
  }, [items]);
  const chosen = items.filter((w) => selected.has(w.id));
  const all = items.length > 0 && chosen.length === items.length;
  const toggle = (id: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const invalidate = [qk.workItemsAll(workspaceId), ...[...new Set(chosen.map((w) => w.deskId))].map((id) => qk.desk(id)), qk.overview(workspaceId)];
  const act = useApiMutation(
    ({ action, ids }: { action: Action; ids: string[] }) => each(ids, ACTIONS[action].run),
    {
      invalidate,
      success: (r) => (r.failed ? t('{done} done, {failed} could not', r) : t('{n} done', { n: r.done })),
      onSuccess: () => setSelected(new Set()),
    },
  );
  const fit = (action: Action) => chosen.filter(ACTIONS[action].can).map((w) => w.id);
  const button = (action: Action, label: string, icon: ReactNode, variant: 'default' | 'outline' = 'outline') => {
    const ids = fit(action);
    if (!ids.length) return null;
    return (
      <Button size="xs" variant={variant} onClick={() => act.mutate({ action, ids })} loading={act.isPending && act.variables?.action === action}>
        {icon} {label}
        {ids.length < chosen.length && <span className="tabular-nums opacity-70">{ids.length}</span>}
      </Button>
    );
  };
  const cancelIds = fit('cancel');
  const decideIds = chosen.filter(decidable).map((w) => w.id);
  const instructIds = chosen.filter(instructable).map((w) => w.id);
  const quick = QUICK.map((status) => ({ status, ids: items.filter((w) => w.status === status).map((w) => w.id) })).filter((q) => q.ids.length);
  const only = (ids: string[]) => chosen.length === ids.length && ids.every((id) => selected.has(id));
  const decide = (approved: boolean) => (
    <DecideDialog approved={approved} ids={decideIds} workspaceId={workspaceId} invalidate={invalidate} onDone={() => setSelected(new Set())}>
      <Button size="xs" variant={approved ? 'default' : 'outline'}>
        {approved ? <Check /> : <X />} {approved ? t('Approve') : t('Reject')}
        {decideIds.length < chosen.length && <span className="tabular-nums opacity-70">{decideIds.length}</span>}
      </Button>
    </DecideDialog>
  );

  return (
    <div className={cn('divide-y', className)}>
      <div className="sticky top-0 z-10 flex min-h-11 flex-wrap items-center gap-2 bg-background px-4 py-2">
        <Checkbox
          checked={all}
          ref={(el) => {
            if (el) el.indeterminate = chosen.length > 0 && !all;
          }}
          onChange={(e) => setSelected(e.target.checked ? new Set(items.map((w) => w.id)) : new Set())}
          aria-label={t('Select all')}
        />
        <span className="mr-1 text-xs text-muted-foreground">{chosen.length ? t('{n} selected', { n: chosen.length }) : t('Select all')}</span>
        {quick.map((q) => (
          <Button
            key={q.status}
            size="xs"
            variant={only(q.ids) ? 'secondary' : 'ghost'}
            aria-pressed={only(q.ids)}
            onClick={() => setSelected(new Set(q.ids))}
            title={t('Select every item that is “{status}”', { status: WORK_ITEM_STATUS[q.status].label })}
            className={only(q.ids) ? undefined : 'text-muted-foreground'}
          >
            {WORK_ITEM_STATUS[q.status].label}
            <span className="tabular-nums opacity-70">{q.ids.length}</span>
          </Button>
        ))}
        {chosen.length > 0 && <span className="basis-full sm:hidden" />}
        {chosen.length > 0 && (
          <>
            {decideIds.length > 0 && (
              <>
                {decide(true)}
                {decide(false)}
              </>
            )}
            {instructIds.length > 0 && (
              <InstructDialog ids={instructIds} invalidate={invalidate} onDone={() => setSelected(new Set())}>
                <Button size="xs" variant="outline">
                  <MessageSquareText /> {t('Instruction')}
                  {instructIds.length < chosen.length && <span className="tabular-nums opacity-70">{instructIds.length}</span>}
                </Button>
              </InstructDialog>
            )}
            {button('start', t('Start'), <Play />, 'default')}
            {button('resume', t('Continue'), <Play />)}
            {button('retry', t('Try again'), <RotateCcw />)}
            {button('pause', t('Pause'), <Pause />)}
            <BulkEditDialog ids={chosen.map((w) => w.id)} workspaceId={workspaceId} invalidate={invalidate} onDone={() => setSelected(new Set())}>
              <Button size="xs" variant="outline">
                <Pencil /> {t('Edit')}
              </Button>
            </BulkEditDialog>
            {cancelIds.length > 0 && (
              <Confirm
                title={t('Cancel {n} Work Items?', { n: cancelIds.length })}
                description={t('The agents stop working on them. Activity and messages so far are kept.')}
                confirmLabel={t('Yes, cancel')}
                cancelLabel={t('No, keep working')}
                destructive
                onConfirm={() => act.mutateAsync({ action: 'cancel', ids: cancelIds })}
              >
                <Button size="xs" variant="outline" className="text-destructive hover:text-destructive">
                  <XCircle /> {t('Cancel')}
                  {cancelIds.length < chosen.length && <span className="tabular-nums opacity-70">{cancelIds.length}</span>}
                </Button>
              </Confirm>
            )}
            <Button size="icon-sm" variant="ghost" className="ml-auto size-7" onClick={() => setSelected(new Set())} aria-label={t('Clear selection')} title={t('Clear selection')}>
              <X />
            </Button>
          </>
        )}
      </div>
      {items.map((w) => {
        const { depth, trailing } = row?.(w) ?? {};
        return (
          <div key={w.id} className={cn('flex items-center', selected.has(w.id) && 'bg-primary/5')}>
            <label className="flex cursor-pointer items-center self-stretch pl-4">
              <Checkbox checked={selected.has(w.id)} onChange={(e) => toggle(w.id, e.target.checked)} aria-label={t('Select {title}', { title: w.title })} />
            </label>
            <WorkItemRow
              item={w}
              workspaceId={workspaceId}
              showDesk={showDesk}
              depth={depth}
              isChild={!!w.parentWorkItemId}
              trailing={trailing}
              className="min-w-0 flex-1 pl-3"
            />
          </div>
        );
      })}
    </div>
  );
}

/** Approves or rejects what the ticked items wait for, with one comment the agents read; the same as on each item. */
function DecideDialog({
  approved,
  ids,
  workspaceId,
  invalidate,
  onDone,
  children,
}: {
  approved: boolean;
  ids: string[];
  workspaceId: string;
  invalidate: readonly (readonly unknown[])[];
  onDone: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  useEffect(() => {
    if (open) setComment('');
  }, [open]);
  const save = useApiMutation(
    () => {
      const body = { comment: comment.trim() || undefined };
      return each(ids, (id) => (approved ? api.workItems.approve(id, body) : api.workItems.reject(id, body)));
    },
    {
      invalidate: [...invalidate, ...ids.map((id) => qk.workItem(id)), qk.approvalsAll(workspaceId)],
      success: (r) =>
        r.failed
          ? t('{done} decided, {failed} could not', r)
          : approved
            ? t('{n} approved', { n: r.done })
            : t('{n} rejected', { n: r.done }),
      onSuccess: () => {
        setOpen(false);
        onDone();
      },
    },
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{approved ? t('Approve {n} Work Items', { n: ids.length }) : t('Reject {n} Work Items', { n: ids.length })}</DialogTitle>
            <DialogDescription>
              {approved
                ? t('Each item is approved as on its own page. Where an agent offered options, none of them is picked.')
                : t('Each item is rejected as on its own page; its agent reads why in the comment.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('Comment')} hint={t('Every agent reads it with the decision.')} optional>
            <Textarea autoFocus value={comment} onChange={(e) => setComment(e.target.value)} rows={4} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant={approved ? 'default' : 'destructive'} loading={save.isPending}>
              {approved ? <Check /> : <X />} {approved ? t('Approve') : t('Reject')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Writes the same instruction into the thread of every ticked item, as a person would on each item's page. */
function InstructDialog({
  ids,
  invalidate,
  onDone,
  children,
}: {
  ids: string[];
  invalidate: readonly (readonly unknown[])[];
  onDone: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  useEffect(() => {
    if (open) setContent('');
  }, [open]);
  const send = useApiMutation(() => each(ids, (id) => api.workItems.sendMessage(id, { content: content.trim() })), {
    invalidate: [...invalidate, ...ids.map((id) => qk.workItem(id))],
    success: (r) => (r.failed ? t('{done} sent, {failed} could not', r) : t('Sent to {n}', { n: r.done })),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (content.trim()) send.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Instruction to {n} Work Items', { n: ids.length })}</DialogTitle>
            <DialogDescription>
              {t('Goes into the thread of each item, as if you wrote it there. A waiting or blocked item goes on with it; a running agent reads it on its next step.')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            autoFocus
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && content.trim()) send.mutate();
            }}
            rows={6}
            placeholder={t('e.g. Use the new price list from the files, then go on.')}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={send.isPending} disabled={!content.trim()}>
              <MessageSquareText /> {t('Send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Sets agent and priority of several Work Items; what stays on "keep" is left as each item has it. */
function BulkEditDialog({
  ids,
  workspaceId,
  invalidate,
  onDone,
  children,
}: {
  ids: string[];
  workspaceId: string;
  invalidate: readonly (readonly unknown[])[];
  onDone: () => void;
  children: ReactNode;
}) {
  const agents = useAgents(workspaceId);
  const [open, setOpen] = useState(false);
  const [agentId, setAgentId] = useState(KEEP);
  const [priority, setPriority] = useState('');
  useEffect(() => {
    if (!open) return;
    setAgentId(KEEP);
    setPriority('');
  }, [open]);
  const body = {
    ...(agentId !== KEEP && { assignedAgentId: agentId === NONE ? null : agentId }),
    ...(priority.trim() && { priority: parsePriority(priority) }),
  };
  const save = useApiMutation(() => each(ids, (id) => api.workItems.update(id, body)), {
    invalidate: [...invalidate, ...ids.map((id) => qk.workItem(id))],
    success: (r) => (r.failed ? t('{done} saved, {failed} could not', r) : t('{n} saved', { n: r.done })),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  const changes = Object.keys(body).length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (changes) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Edit {n} Work Items', { n: ids.length })}</DialogTitle>
            <DialogDescription>{t('What you leave on “keep” stays as each item has it.')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Agent')}>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={KEEP}>{t('Keep')}</SelectItem>
                <SelectItem value={NONE}>{t('No agent')}</SelectItem>
                {(agents.data ?? []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('Priority')} hint={t('0–100, 50 = normal. Higher is worked on first.')} optional>
            <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} min={0} max={MAX_WORK_ITEM_PRIORITY} step={1} placeholder={t('Keep')} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!changes}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
